<script setup lang="ts">
import { computed } from 'vue';

type Segment = { text: string; match: boolean };

const { text, query } = defineProps<{
  text: string;
  /** Every case-insensitive occurrence of this is marked. Blank marks nothing. */
  query?: string;
}>();

// Interpolation keeps the segments plain text, so a query full of regex or HTML
// metacharacters is inert here and never needs escaping.
const segments = computed<Segment[]>(() => {
  const needle = query?.trim().toLowerCase() ?? '';

  if (needle.length === 0 || text.length === 0) {
    return [{ text, match: false }];
  }

  const haystack = text.toLowerCase();
  const parts: Segment[] = [];
  let cursor = 0;

  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, cursor)) {
    if (at > cursor) {
      parts.push({ text: text.slice(cursor, at), match: false });
    }

    parts.push({ text: text.slice(at, at + needle.length), match: true });
    cursor = at + needle.length;
  }

  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), match: false });
  }

  return parts;
});
</script>

<template>
  <span class="ui-highlighted-text"
    ><template v-for="(segment, index) in segments" :key="index"
      ><mark v-if="segment.match" class="ui-highlighted-text__match">{{ segment.text }}</mark
      ><template v-else>{{ segment.text }}</template></template
    ></span
  >
</template>

<style scoped lang="scss">
.ui-highlighted-text {
  min-width: 0;

  // Figma 3291:198358 marks the hit with colour and weight, no fill and no
  // inset. The UA sheet gives `mark` a yellow background, so that is cleared.
  &__match {
    background: none;
    color: $color-brand-500;
    font-weight: $font-weight-semibold;
  }
}
</style>
